import type { NodeDefinition } from "@docit/flow-editor";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AvailableThinker } from "~/api/aiAdminTypes";
import type { CanopyScope } from "~/api/canopyTypes";
import { useApi } from "~/api/provider";
import {
  type DreamerExperimentArtifact,
  type FlowPostmortem,
  REASONING_EFFORTS,
  type ReasoningEffort,
  type ReasoningOperation,
} from "~/api/reasoningTypes";
import type { Run } from "~/api/types";
import { pinnedEvidence } from "~/features/reasoning/candidateDraft";
import { ReasoningCandidateEditor } from "~/features/reasoning/ReasoningCandidateEditor";
import { ReasoningCriterionReview } from "~/features/reasoning/ReasoningCriterionReview";
import { ReasoningMemoryGraph as MemoryGraphView } from "~/features/reasoning/ReasoningMemoryGraph";
import {
  isTerminalRunStatus,
  purposeOf,
  readSession,
} from "~/features/reasoning/reasoningSelectors";
import {
  clearReasoningRecovery,
  criterionReviewKey,
  reasoningRunActionKey,
} from "~/features/reasoning/recoveryStorage";
import { useReasoningSession } from "~/features/reasoning/useReasoningSession";
import { budgetedLimitsOf, useReasoningSupport } from "~/features/reasoning/useReasoningSupport";
import { ActionRequired } from "~/foundation/components/ActionRequired";
import { Button } from "~/foundation/components/Button";
import { PostmortemSection } from "./PostmortemSection";
import { ReasoningAdvancedSection } from "./ReasoningAdvancedSection";
import { ReasoningArtifactsSection } from "./ReasoningArtifactsSection";
import { type ChainLaunch, ReasoningChainEditor } from "./ReasoningChainEditor";
import { ReasoningSearchSection } from "./ReasoningSearchSection";
import { StepForm } from "./StepForm";
import { isOperation, usePendingAction } from "./usePendingAction";

/** A started session's context window when the flow did not say; the server allows 4,096–1,000,000. */
const DEFAULT_WINDOW = 8192;
/** The answer reserve is sized from the agent's own `maxOutputTokens`, never above this. */
const MAX_DEFAULT_RESERVE = 4096;

/** A run's observable reasoning record, local agent launcher, and Dreamer handoff. */
export function ReasoningRunTab({
  run,
  sessionId,
  nodeDefinition,
  onDraftDirtyChange,
}: {
  run: Run;
  sessionId?: string;
  nodeDefinition?: NodeDefinition;
  onDraftDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const terminal = isTerminalRunStatus(run.status);
  const record = useReasoningSession({ runId: run.id, sessionId, terminal });
  const { session, trace, memoryGraph, sourceBarriers } = record;
  const { support, budgetedLimits } = useReasoningSupport();
  const [candidateDirty, setCandidateDirty] = useState(false);
  const [reviewDirty, setReviewDirty] = useState(false);
  const [postmortem, setPostmortem] = useState<FlowPostmortem | null>(null);
  const [experiments, setExperiments] = useState<DreamerExperimentArtifact[]>([]);
  const [thinkers, setThinkers] = useState<AvailableThinker[]>([]);
  const [scopes, setScopes] = useState<CanopyScope[]>([]);
  const [scopeSlug, setScopeSlug] = useState("");
  const [thinkerId, setThinkerId] = useState("");
  const [evaluatorId, setEvaluatorId] = useState("");
  const [effort, setEffort] = useState<ReasoningEffort>("medium");
  const [effortMode, setEffortMode] = useState<"budgeted" | "native">("budgeted");
  const [query, setQuery] = useState(run.name);
  const [criterion, setCriterion] = useState(
    "Answer the request with evidence from the selected Canopy scope.",
  );
  const [operation, setOperation] = useState<ReasoningOperation | null>(null);
  const [branchGroupId, setBranchGroupId] = useState("");
  const [followupStatus, setFollowupStatus] = useState<
    "met" | "partially-met" | "unmet" | "unknown"
  >("unknown");
  const [objectiveId, setObjectiveId] = useState("");
  const [followupObservation, setFollowupObservation] = useState("");
  const [loadMoreError, setLoadMoreError] = useState("");
  const [artifactBusy, setArtifactBusy] = useState(false);
  const [artifactDirty, setArtifactDirty] = useState(false);

  const refreshDreamer = async () => {
    setPostmortem(await api.reasoning.postmortem(run.id));
    if (run.aiFlowSlug) setExperiments(await api.reasoning.dreamerExperiments(run.id));
  };
  const refresh = async () => {
    await record.refresh();
    await refreshDreamer();
  };
  const action = usePendingAction(run.id, refresh);
  const { pendingAction, running } = action;
  const busy = action.busy || artifactBusy;
  const act = async (spec?: Parameters<typeof action.act>[0]) => {
    const outcome = await action.act(spec);
    if (outcome?.kind === "execute" && isOperation(outcome.result)) setOperation(outcome.result);
    if (outcome?.kind === "followup") setFollowupObservation("");
    return outcome;
  };

  useEffect(() => () => onDraftDirtyChange?.(false), [onDraftDirtyChange]);
  useEffect(() => {
    onDraftDirtyChange?.(candidateDirty || reviewDirty || artifactDirty || pendingAction !== null);
  }, [candidateDirty, reviewDirty, artifactDirty, pendingAction, onDraftDirtyChange]);

  useEffect(() => {
    let live = true;
    setPostmortem(null);
    setOperation(null);
    setExperiments([]);
    Promise.allSettled([
      api.reasoning.postmortem(run.id),
      api.thinkers.available(),
      api.canopy.scopes(),
      run.aiFlowSlug ? api.reasoning.dreamerExperiments(run.id) : Promise.resolve([]),
    ]).then(([report, agents, canopy, artifacts]) => {
      if (!live) return;
      if (report.status === "fulfilled") setPostmortem(report.value);
      if (artifacts.status === "fulfilled") setExperiments(artifacts.value);
      if (agents.status === "fulfilled")
        setThinkers(agents.value.filter((x) => x.usable && x.isLocal && x.isAi));
      if (canopy.status === "fulfilled") {
        setScopes(canopy.value.filter((s) => s.enabled));
        setScopeSlug((prior) => prior || canopy.value.find((s) => s.enabled)?.slug || "");
      }
    });
    return () => {
      live = false;
    };
  }, [api, run.id, run.aiFlowSlug]);

  const { candidate, evaluation, accepted } = readSession(session);
  // A finished, accepted run keeps no replay journals: nothing on it will be retried.
  useEffect(() => {
    if (!terminal) return;
    if (!pendingAction && !running) clearReasoningRecovery(reasoningRunActionKey(run.id));
    if (session && accepted && !reviewDirty) clearReasoningRecovery(criterionReviewKey(session.id));
  }, [terminal, pendingAction, running, run.id, session, accepted, reviewDirty]);

  const selected = thinkers.find((x) => x.id === thinkerId) ?? thinkers[0];
  const evaluator = thinkers.find((x) => x.id === evaluatorId);
  const checkingThinker = evaluator ?? selected;
  /** One execute input for "Run agent" and the chain's "Continue from here": same agent, same switches. */
  const launch: ChainLaunch | undefined = selected?.model
    ? {
        thinkerId: selected.id,
        model: selected.model,
        effort,
        effortMode,
        localOnly: true,
        evaluatorThinkerId: evaluator?.id,
        evaluatorModel: evaluator?.model || undefined,
        branchGroupId: branchGroupId || undefined,
        method: nodeDefinition?.reasoning?.method,
        mlx: nodeDefinition?.reasoning?.mlx,
        temperature:
          typeof nodeDefinition?.temperature === "number" ? nodeDefinition.temperature : undefined,
      }
    : undefined;
  const latestOperation = operation ?? trace?.operations.at(-1) ?? null;
  const purpose = purposeOf(session);
  const objective =
    purpose?.objectives.find((item) => item.id === objectiveId) ?? purpose?.objectives[0];
  const followups = session?.records.filter((r) => r.kind === "objective-followup") ?? [];
  const availableEvidence = session ? pinnedEvidence(session) : [];
  const latestEvent = trace?.trace.events.at(-1);
  const manifest = trace?.traceManifest;
  const error = record.error || action.error || loadMoreError;
  const stages = session
    ? [
        { label: t("Context"), done: session.records.some((r) => r.kind === "context") },
        {
          label: t("Candidate"),
          done: session.records.some((r) => r.kind === "candidate" && r.eligible),
        },
        {
          label: t("Evaluation"),
          done: session.records.some((r) => r.kind === "evaluation" && r.eligible),
        },
        { label: t("Acceptance"), done: !!session.currentAcceptanceRecordId },
        { label: t("Follow-up"), done: followups.length > 0 },
      ]
    : [];
  const caps = budgetedLimitsOf(support) ?? budgetedLimits;
  const capsProse = caps
    ? t(
        "Budgeted effort caps each output at {{caps}} tokens (further limited by the pinned reserve).",
        {
          caps: REASONING_EFFORTS.map((level) => caps[level]).join(" / "),
        },
      )
    : t("Budgeted effort caps each output at the server's published limits.");

  const startReasoning = async () => {
    if (!selected?.model) return;
    const capabilities = await api.reasoning.localCapabilities(selected.id).catch(() => null);
    const outputReserve = Math.max(
      1,
      Math.min(capabilities?.maxOutputTokens ?? MAX_DEFAULT_RESERVE, MAX_DEFAULT_RESERVE),
    );
    await act({
      kind: "start",
      input: {
        runId: run.id,
        scopeSlug,
        query,
        model: selected.model,
        modelContextWindow: DEFAULT_WINDOW,
        outputReserve,
        criteria: [
          { id: "answer", description: criterion.trim(), hard: true, requiresEvidence: true },
        ],
      },
    });
  };

  return (
    <div className="flex flex-col gap-5 rounded-md border border-divider p-4">
      <div>
        <h3 className="text-lg font-semibold">{t("Reasoning agents")}</h3>
        <p className="text-small text-text-secondary">
          {t("Review the answer, check the evidence, and choose whether to accept it.")}
        </p>
        <details className="mt-2 text-small text-text-secondary">
          <summary className="cursor-pointer">{t("Model and budget details")}</summary>
          <p className="text-small text-text-secondary">
            {t(
              "Runs use bounded Canopy context and retain observable decisions, evidence, evaluations, and provenance.",
            )}{" "}
            {capsProse} {t("Native effort depends on model support.")}
          </p>
        </details>
        {run.aiFlowSlug && (
          <Link to="/flows/$slug/edit" params={{ slug: run.aiFlowSlug }}>
            {t("Edit this flow visually")}
          </Link>
        )}
      </div>
      {error ? <ActionRequired error={error} /> : null}
      {session ? (
        <section
          aria-label={t("Decision review")}
          className="space-y-3 rounded-lg border border-accent/30 bg-accent/5 p-4"
        >
          <h3 className="font-semibold">{t("Decision review")}</h3>
          <p className="text-small text-text-secondary">
            {accepted
              ? t("This result has been accepted.")
              : candidate
                ? t("Review the proposed answer and its checks before accepting it.")
                : t("No answer is ready for review yet.")}
          </p>
          {candidate && (
            <article className="rounded border border-divider p-3">
              <h4 className="font-semibold">{t("Candidate")}</h4>
              <p className="whitespace-pre-wrap">{String(candidate.payload.content ?? "")}</p>
            </article>
          )}
          {evaluation && (
            <p>
              {t("Evaluation: {{verdict}}", {
                verdict:
                  evaluation.payload.qualified === true ? t("qualified") : t("not qualified"),
              })}
            </p>
          )}
          {candidate?.eligible &&
            evaluation?.eligible &&
            evaluation.payload.qualified === true &&
            !accepted && (
              <Button
                disabled={busy || candidateDirty || reviewDirty || artifactDirty}
                onClick={() =>
                  void act({
                    kind: "accept",
                    input: {
                      sessionId: session.id,
                      expectedSessionRevision: session.revision,
                      candidateRecordId: candidate.id,
                      evaluationRecordId: evaluation.id,
                    },
                  })
                }
              >
                {t("Accept evaluated result")}
              </Button>
            )}
        </section>
      ) : null}
      {pendingAction && !running && (
        <Button onClick={() => void act()}>{t("Retry last command / reload result")}</Button>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          {t("Local agent")}
          <select
            aria-label={t("Local reasoning agent")}
            value={selected?.id ?? ""}
            onChange={(e) => setThinkerId(e.target.value)}
            className="rounded border border-divider bg-bg px-2 py-1"
          >
            {thinkers.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} · {x.model || x.provider}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          {t("Evaluator agent")}
          <select
            aria-label={t("Local evaluator agent")}
            value={evaluatorId}
            onChange={(e) => setEvaluatorId(e.target.value)}
            className="rounded border border-divider bg-bg px-2 py-1"
          >
            <option value="">{t("Same as generator")}</option>
            {thinkers.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} · {x.model || x.provider}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          {t("Effort mode")}
          <select
            aria-label={t("Reasoning effort mode")}
            value={effortMode}
            disabled={busy}
            onChange={(e) => setEffortMode(e.target.value as typeof effortMode)}
          >
            <option value="budgeted">{t("Budgeted output")}</option>
            <option value="native">{t("Native model effort")}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          {t("Effort")}
          <select
            aria-label={t("Reasoning effort")}
            value={effort}
            onChange={(e) => setEffort(e.target.value as ReasoningEffort)}
            className="rounded border border-divider bg-bg px-2 py-1"
          >
            {REASONING_EFFORTS.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        {session && (
          <Button
            disabled={
              busy || !launch || session.records.some((row) => row.kind === "search-checkpoint")
            }
            onClick={() => {
              if (!launch) return;
              void act({ kind: "execute", input: { ...launch, sessionId: session.id } });
            }}
          >
            {t("Run agent")}
          </Button>
        )}
        <Button disabled={busy} onClick={() => void refresh().catch(() => undefined)}>
          {t("Refresh")}
        </Button>
      </div>
      {session && (
        <p className="text-small text-text-secondary">
          {t(
            "Run agent generates and evaluates in one operation. Use the recorded step, criterion review, candidate editor and acceptance controls below to steer the retained chain.",
          )}
          {nodeDefinition?.reasoning?.method &&
            ` ${t("This session uses the saved node's authored step and review methods. Edit the flow and start a new revision to change them.")}`}
        </p>
      )}
      {thinkers.length === 0 && (
        <p className="text-text-secondary">
          {t("No enabled local AI agent is available. Add one in AI settings.")}
        </p>
      )}
      {!session && record.loaded && (
        <div className="flex flex-col gap-3">
          <label>
            {t("Canopy scope")}
            <select
              aria-label={t("Canopy scope")}
              value={scopeSlug}
              onChange={(e) => setScopeSlug(e.target.value)}
              className="ms-2 rounded border border-divider bg-bg px-2 py-1"
            >
              {scopes.map((s) => (
                <option key={s.id} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            {t("Question")}{" "}
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="rounded border border-divider bg-bg p-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            {t("Acceptance criterion")}{" "}
            <textarea
              value={criterion}
              onChange={(e) => setCriterion(e.target.value)}
              className="rounded border border-divider bg-bg p-2"
            />
          </label>
          <Button
            disabled={busy || !scopeSlug || !selected?.model || !criterion.trim() || !query.trim()}
            onClick={() => void startReasoning()}
          >
            {t("Start reasoning")}
          </Button>
        </div>
      )}
      {session && (
        <section className="flex flex-col gap-2">
          <div className="rounded border border-divider p-3" aria-live="polite">
            <p className="text-small text-text-secondary">{t("Current attention")}</p>
            <p>
              <strong>{purpose?.intent.in ?? run.name}</strong> ·{" "}
              {t("toward {{toward}}", {
                toward: purpose?.intent.toward ?? t("a supported result"),
              })}
            </p>
            <p>
              {t("Latest recorded action:")}{" "}
              {latestEvent
                ? `${latestEvent.sequence}. ${latestEvent.kind}`
                : t("waiting for the first event")}
              {latestEvent?.payload.summary ? ` · ${String(latestEvent.payload.summary)}` : ""}
            </p>
            <p className="text-small text-text-secondary">
              {t("Graph projection: {{status}}", { status: memoryGraph?.status ?? t("loading") })}
            </p>
          </div>
          <ol aria-label={t("Reasoning stages")} className="grid grid-cols-2 gap-2 md:grid-cols-5">
            {stages.map((stage, index) => (
              <li
                key={stage.label}
                className={`rounded border p-2 ${stage.done ? "border-green-600" : "border-divider"}`}
              >
                <strong>
                  {index + 1}. {stage.label}
                </strong>
                <br />
                {stage.done ? t("Recorded") : t("Pending")}
              </li>
            ))}
          </ol>
          <p>
            <strong>{t("Session:")}</strong> {session.state} ·{" "}
            {t("revision {{revision}}", { revision: session.revision })}
            {accepted ? ` · ${t("accepted")}` : ""}
          </p>
          {latestOperation && (
            <p>
              {t("Last operation: {{status}}", { status: latestOperation.status })}
              {latestOperation.error ? ` · ${latestOperation.error}` : ""} ·{" "}
              {t("{{n}} tokens", { n: latestOperation.inputTokens + latestOperation.outputTokens })}
            </p>
          )}
          <ReasoningCriterionReview
            session={session}
            disabled={busy}
            onSessionChange={(updated) => {
              record.replaceSession(updated);
              void record.refresh().catch(() => undefined);
            }}
            onDirtyChange={setReviewDirty}
          />
          <StepForm
            session={session}
            events={trace?.trace.events ?? []}
            availableEvidence={availableEvidence}
            disabled={busy}
            onRecord={async (input) =>
              (
                await act({
                  kind: "step",
                  input: {
                    sessionId: session.id,
                    expectedSessionRevision: session.revision,
                    ...input,
                  },
                })
              )?.kind === "step"
            }
          />
          {memoryGraph && <MemoryGraphView graph={memoryGraph} session={session} trace={trace} />}
          <h4 className="font-semibold">{t("Observable reasoning chain")}</h4>
          <p className="text-small text-text-secondary">
            {record.polling
              ? t(
                  "New events appear here about every three seconds while this tab is open (every fifteen after two minutes). These are retained actions, model output summaries and evaluations.",
                )
              : t("This run is finished and accepted; its chain no longer changes.")}
          </p>
          <ol
            className="flex flex-col gap-1"
            aria-label={t("Reasoning event timeline")}
            aria-live="polite"
          >
            {(trace?.trace.events ?? []).map((event) => (
              <li key={event.id} className="rounded border border-divider p-2">
                <strong>
                  {event.sequence}. {event.kind}
                </strong>
                <span className="ms-2 text-text-secondary">
                  {String(
                    event.payload.summary ?? event.payload.status ?? event.payload.kind ?? "",
                  )}
                </span>
                {(typeof event.payload.rationaleSummary === "string" ||
                  typeof event.payload.reason === "string") && (
                  <p className="text-small">
                    {String(event.payload.rationaleSummary ?? event.payload.reason)}
                  </p>
                )}
                {(Array.isArray(event.payload.referencedRecordIds) ||
                  Array.isArray(event.payload.causedByEventIds)) && (
                  <details>
                    <summary>{t("Inputs and causes")}</summary>
                    <pre className="overflow-auto text-small">
                      {JSON.stringify(
                        {
                          inputs: event.payload.referencedRecordIds,
                          causes: event.payload.causedByEventIds,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </details>
                )}
              </li>
            ))}
          </ol>
          {trace?.trace.nextSequence != null && (
            <Button
              disabled={busy}
              onClick={() => {
                setLoadMoreError("");
                record
                  .loadMore()
                  .catch((cause: unknown) =>
                    setLoadMoreError(cause instanceof Error ? cause.message : String(cause)),
                  );
              }}
            >
              {t("Load more events")}
            </Button>
          )}
          {trace && manifest && (
            <div className="flex flex-col gap-1 text-small text-text-secondary">
              <p>
                {t("Trace coverage: {{coverage}}.", { coverage: trace.traceCoverage })}{" "}
                {t("Event ledger: {{recorded}} recorded through #{{watermark}}.", {
                  recorded: manifest.recordedEventCount,
                  watermark: manifest.eventSequenceHighWatermark,
                })}
              </p>
              <p role={manifest.missingRangeCount > 0 ? "status" : undefined}>
                {t("Missing ranges: {{n}}.", { n: manifest.missingRangeCount })}
                {manifest.missingRanges.map((range) => (
                  <span key={`${range.from}-${range.to}`}>
                    {" "}
                    {t("Missing #{{from}}–{{to}}.", { from: range.from, to: range.to })}
                  </span>
                ))}
              </p>
              <p
                role={
                  manifest.providerReceiptGapCount > 0 || manifest.operationJournalGapCount > 0
                    ? "status"
                    : undefined
                }
              >
                {t("Provider receipt gaps: {{n}}.", { n: manifest.providerReceiptGapCount })}{" "}
                {t("Operation journal gaps: {{n}}.", { n: manifest.operationJournalGapCount })}
                {manifest.providerReceiptGapCount > 0 || manifest.operationJournalGapCount > 0
                  ? ` ${t("Open Advanced › Model operations to see which operation is incomplete.")}`
                  : ""}
              </p>
            </div>
          )}
          <ReasoningChainEditor
            key={`chain-${session.id}`}
            session={session}
            disabled={busy}
            act={act}
            launch={launch}
            onRevised={() => void refresh().catch(() => undefined)}
          />
          <ReasoningCandidateEditor
            key={`candidate-${session.id}`}
            session={session}
            disabled={busy}
            onDirtyChange={setCandidateDirty}
            onSessionChange={(updated) => {
              record.replaceSession(updated);
              setOperation(null);
              void record.refresh().catch(() => undefined);
            }}
          />
          {purpose && (
            <section className="flex flex-col gap-2 border-t border-divider pt-3">
              <h4 className="font-semibold">{t("Intent and objective follow-up")}</h4>
              <p>
                <strong>{purpose.intentKey}</strong> · {purpose.intent.toward}
              </p>
              {purpose.objectives.map((item) => (
                <p key={item.id}>
                  {item.outcome} · {t("success signal: {{signal}}", { signal: item.successSignal })}
                </p>
              ))}
              {followups.map((item) => (
                <p key={item.id}>
                  {t("Reported {{objective}}: {{status}} · {{observation}}", {
                    objective: String(item.payload.objectiveId),
                    status: String(item.payload.status),
                    observation: String(item.payload.observation),
                  })}
                </p>
              ))}
              {terminal && objective && (
                <div className="flex flex-col gap-2">
                  <label>
                    {t("Objective")}{" "}
                    <select
                      value={objective.id}
                      onChange={(e) => setObjectiveId(e.target.value)}
                      className="rounded border border-divider p-1"
                    >
                      {purpose.objectives.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.outcome}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    {t("Observed status")}{" "}
                    <select
                      value={followupStatus}
                      onChange={(e) => setFollowupStatus(e.target.value as typeof followupStatus)}
                      className="rounded border border-divider p-1"
                    >
                      {(["unknown", "met", "partially-met", "unmet"] as const).map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </label>
                  <textarea
                    aria-label={t("Objective follow-up observation")}
                    placeholder={t("What happened after the reasoning was used?")}
                    value={followupObservation}
                    onChange={(e) => setFollowupObservation(e.target.value)}
                    className="rounded border border-divider p-2"
                  />
                  <Button
                    disabled={busy || !followupObservation.trim()}
                    onClick={() =>
                      void act({
                        kind: "followup",
                        input: {
                          sessionId: session.id,
                          expectedSessionRevision: session.revision,
                          objectiveId: objective.id,
                          status: followupStatus,
                          observation: followupObservation.trim(),
                        },
                      })
                    }
                  >
                    {t("Record objective follow-up")}
                  </Button>
                </div>
              )}
            </section>
          )}
          <ReasoningArtifactsSection
            key={`artifacts-${session.id}`}
            session={session}
            busy={action.busy}
            refresh={refresh}
            onBusyChange={setArtifactBusy}
            onDirtyChange={setArtifactDirty}
          />
          <ReasoningSearchSection
            session={session}
            thinker={selected?.model ? { id: selected.id, model: selected.model } : undefined}
            evaluator={
              checkingThinker?.model
                ? { id: checkingThinker.id, model: checkingThinker.model }
                : undefined
            }
            effort={effort}
            busy={busy}
            act={act}
          />
          <ReasoningAdvancedSection
            session={session}
            trace={trace}
            sourceBarriers={sourceBarriers}
            busy={busy}
            act={act}
            thinker={selected?.model ? { id: selected.id, model: selected.model } : undefined}
            effort={effort}
            branchGroupId={branchGroupId}
            onBranchGroupChange={setBranchGroupId}
          />
        </section>
      )}
      {["flow", "reasoning"].includes(run.kind.toLowerCase()) && terminal && (
        <PostmortemSection
          run={run}
          postmortem={postmortem}
          experiments={experiments}
          thinkerId={selected?.id}
          effort={effort}
          effortMode={effortMode}
          busy={busy}
          act={act}
        />
      )}
    </div>
  );
}
